import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import { Store, LocalOffer } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  selectCategory,
  selectSortBy,
  selectSearchQuery,
  setCategory,
  setSortBy,
  setSearchQuery,
  resetFilters,
  selectHasFiltersApplied,
} from "../../store/slices/offersSlice";
import { useGetOffersByMallQuery } from "../../features/offers/offersApiSlice";
import { useGetMallByIdQuery } from "../../features/malls/mallsApiSlice";
import { useDebounce } from "../../hooks/useDebounce";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  OfferCard,
  EmptyState,
  OffersHero,
  OffersFilterBar,
  OffersFilterBarSkeleton,
  OffersSkeleton,
} from "../../components/Offers";
import ScrollIndicator, {
  BottomFadeOverlay,
} from "../../components/common/ScrollIndicator";
import { OfferCategory } from "../../types/offers";
import { PAGINATION } from "../../config/constants";

const Offers: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { mallId: selectedMallId } = useParams<{ mallId: string }>();
  const category = useAppSelector(selectCategory);
  const sortBy = useAppSelector(selectSortBy);
  const searchQuery = useAppSelector(selectSearchQuery);
  const hasFilters = useAppSelector(selectHasFiltersApplied);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedOffers, setAllLoadedOffers] = useState<any[]>([]);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);

  // Get mall from navigation state (passed from MallHub) or fetch as fallback
  const mallFromState = location.state?.mall;
  const { data: mallFromApi } = useGetMallByIdQuery(selectedMallId || "", {
    skip: !selectedMallId || !!mallFromState, // Skip API call if we have mall from state
  });
  const selectedMall = mallFromState || mallFromApi;

  // Fetch offers with filters and pagination
  const {
    data: offersData,
    isLoading: offersLoading,
    error: offersError,
    isFetching: offersFetching,
  } = useGetOffersByMallQuery(
    {
      mallId: selectedMallId || "",
      category: category !== "all" ? category : undefined,
      sortBy,
      searchQuery: debouncedSearchQuery,
      page: currentPage,
      limit: PAGINATION.PAGE_LIMIT,
    },
    {
      skip: !selectedMallId, // Don't fetch if no mall selected
    }
  );

  // Pagination info
  const pagination = offersData?.pagination;
  const hasMore = pagination ? pagination.page < pagination.totalPages : false;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setAllLoadedOffers([]);
  }, [selectedMallId, category, sortBy, debouncedSearchQuery]);

  // Accumulate offers when new page loads
  useEffect(() => {
    if (offersData?.offers !== undefined) {
      setAllLoadedOffers((prev) => {
        // If it's page 1, replace all offers (filters changed)
        if (currentPage === 1) {
          return offersData.offers;
        }
        // Otherwise, append new offers
        const newOffers = offersData.offers.filter(
          (newOffer) =>
            !prev.some((existingOffer) => existingOffer.id === newOffer.id)
        );
        return [...prev, ...newOffers];
      });
    }
  }, [offersData, currentPage]);

  const stats = offersData?.stats || {
    total: 0,
    expiringSoon: 0,
    newOffers: 0,
  };

  // Load next page
  const loadMore = useCallback(() => {
    if (hasMore && !offersFetching) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore, offersFetching]);

  // Setup infinite scroll
  useInfiniteScroll(loadMore, hasMore, offersFetching);

  // Use accumulated offers
  const offers = allLoadedOffers;

  // Loading state
  const isLoading = selectedMallId && offersLoading;

  // Show filter bar after mall selection
  useEffect(() => {
    if (selectedMallId) {
      setTimeout(() => setShowFilterBar(true), 300);
    } else {
      setShowFilterBar(false);
    }
  }, [selectedMallId]);

  // Smooth scroll effect - content slides up over hero
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const currentScrollY = window.scrollY;
        const maxOffset = 350;
        const offset = Math.min(currentScrollY, maxOffset);
        contentRef.current.style.transform = `translateY(-${offset}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCategoryChange = (newCategory: OfferCategory | "all") => {
    dispatch(setCategory(newCategory));
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    dispatch(
      setSortBy(
        event.target.value as "newest" | "expiring" | "discount" | "popular"
      )
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(event.target.value));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
        pb: 0,
      }}
    >
      {/* Hero Section */}
      <OffersHero selectedMall={selectedMall} stats={stats} />

      {/* Main Content - Slides up over hero */}
      <Box
        ref={contentRef}
        sx={{
          position: "relative",
          zIndex: 1100,
          background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
          borderTopLeftRadius: { xs: 16, md: 24 },
          borderTopRightRadius: { xs: 16, md: 24 },
          boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
          willChange: "transform",
          mt: -5,
        }}
      >
        {/* Filter Bar - Show when mall is selected */}
        {selectedMallId && isLoading ? (
          <OffersFilterBarSkeleton />
        ) : (
          <OffersFilterBar
            showFilterBar={showFilterBar}
            selectedMall={selectedMall}
            category={category}
            sortBy={sortBy}
            searchQuery={searchQuery}
            hasFilters={hasFilters}
            onCategoryChange={handleCategoryChange}
            onSortChange={handleSortChange}
            onSearchChange={handleSearchChange}
            onResetFilters={handleResetFilters}
          />
        )}

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ mt: 4, mb: 0, pb: { xs: 3, md: 4 } }}>
          {!selectedMallId ? (
            // No mall selected
            <EmptyState
              icon={<Store sx={{ fontSize: 80, color: "text.secondary" }} />}
              title="No Mall Selected"
              description="Please select a mall from the Explore page to view offers"
              action={
                <Box
                  sx={{
                    mt: 3,
                    px: 3,
                    py: 1.5,
                    bgcolor: "primary.main",
                    color: "white",
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "primary.dark",
                      transform: "translateY(-2px)",
                    },
                  }}
                  onClick={() => navigate("/explore")}
                >
                  <Typography variant="button" sx={{ fontWeight: 600 }}>
                    Go to Explore
                  </Typography>
                </Box>
              }
            />
          ) : isLoading ? (
            // Loading state
            <OffersSkeleton />
          ) : offersError ? (
            // Error state
            <EmptyState
              icon={<LocalOffer sx={{ fontSize: 80, color: "error.main" }} />}
              title="Failed to Load Offers"
              description="Unable to fetch offers. Please try again later."
            />
          ) : offers.length === 0 ? (
            // No offers found
            <EmptyState
              icon={
                <LocalOffer sx={{ fontSize: 80, color: "text.secondary" }} />
              }
              title="No Offers Found"
              description={
                hasFilters
                  ? "Try adjusting your filters or search terms"
                  : "No offers are currently available for this mall"
              }
              action={
                hasFilters ? (
                  <Chip
                    label="Reset Filters"
                    onClick={handleResetFilters}
                    sx={{
                      background:
                        "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                      color: "white",
                      fontWeight: 600,
                      px: 3,
                      py: 3,
                      fontSize: "0.9rem",
                    }}
                  />
                ) : undefined
              }
            />
          ) : (
            // Offers Grid
            <Box sx={{ position: "relative" }}>
              <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
                {offers.map((offer, index) => (
                  <Grid item xs={12} sm={6} md={4} key={offer.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.4 }}
                    >
                      <OfferCard offer={offer} />
                    </motion.div>
                  </Grid>
                ))}
              </Grid>

              {/* Loading indicator for infinite scroll */}
              {offersFetching && currentPage > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 4,
                    mt: 2,
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography
                    variant="body2"
                    sx={{ ml: 2, color: "text.secondary" }}
                  >
                    Loading more offers...
                  </Typography>
                </Box>
              )}

              {/* End of results message */}
              {!hasMore && offers.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 4,
                    mt: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontStyle: "italic" }}
                  >
                    You've reached the end of the offers
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Container>
      </Box>

      {/* Scroll Indicator for Mobile Users */}
      <ScrollIndicator color="primary.main" bottomOffset={100} />
      <BottomFadeOverlay color="#f5f7fa" height={100} />
    </Box>
  );
};

export default Offers;
